import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import GenericFormDrawer from '../../components/GenericFormDrawer';
import Button from '../../components/ui/Button';
import api from '../../services/api';
import { formatDate } from '../../utils/Utils';
import {
  Badge, INSIGHT_STATUS_COLORS, SEVERITY_COLORS, SUGGESTED_ACTION_TYPE, insightBody, insightTitle,
} from './config';
import { evidenceRows, insightSourceLink, profileLink } from '../performance-ai-center/config';

// Analitik Plan 4 (spec §6.2) — bulgu detayı (İK): metin, kanıt alt kalemleri, bağlı aksiyonlar ve kullanıcı geçişleri
// (incelemeye al, aksiyon aç, kapat, geçersiz işaretle). Kabuk GenericFormDrawer; birincil düğme durumun sıradaki
// adımıdır (new → İncelemeye Al, actioned → Kapat), diğer işlemler gövdedeki düğmelerdedir.
const Text = styled.p`
  margin: 0 0 16px;
  font-size: ${({ theme }) => theme.metrics.font.size.md};
  color: ${({ theme }) => theme.colors.text.primary};
  line-height: 1.5;
`;

const MetaRow = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 8px;
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
`;

const MetaLabel = styled.span`
  min-width: 160px;
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const MetaValue = styled.span`
  color: ${({ theme }) => theme.colors.text.dark?.little || theme.colors.text.primary};
`;

const SectionTitle = styled.h4`
  margin: 20px 0 10px;
  padding-top: 14px;
  border-top: 1px solid ${({ theme }) => theme.colors.surface?.border || '#E8ECF0'};
  font-size: ${({ theme }) => theme.metrics.font.size.md};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Item = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 8px 12px;
  margin-bottom: 6px;
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.bgColor};
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
`;

const Notice = styled.div`
  margin: 0 0 16px;
  padding: 10px 12px;
  border-radius: 8px;
  background: #FFFBEB;
  color: #92400E;
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
`;

const Buttons = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 16px;
`;

// P7a (E15 "Bulgu detayında gözlem, hesaplama, kaynaklar, sınırlamalar, olası açıklama ve önerilen aksiyon") — hesaplama kanıtın
// sayısal alanlarıdır (LLM yok, güven yüzdesi yok); "olası açıklama" ayrı etiketlenir ve neden kanıtı olarak sunulmaz. Kanıt
// bağlantısı kaynak ekranın aynı dönemine ve veri sürümüne (kural/eşik sürümü, veri tarihi) gider.
const Hint = styled.p`
  margin: 0 0 8px;
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.muted};
  line-height: 1.5;
`;

function InsightExplain({ insight, isHr, onNavigate }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const rows = evidenceRows(insight);
  const rule = insight.ruleCode;
  const link = insightSourceLink(insight, isHr);
  const suggested = SUGGESTED_ACTION_TYPE[rule];
  return (
    <>
      <SectionTitle>{t('performance.aiCenter.detail.calculation')}</SectionTitle>
      {rows.length === 0 ? <Hint>{t('performance.aiCenter.detail.noCalculation')}</Hint> : rows.map((r) => (
        <Item key={r.key} data-evidence={r.key}>
          <span>{t(`performance.aiCenter.evidence.${r.key}`, { defaultValue: r.key })}</span>
          <span>{r.percent ? `%${r.value}` : r.value}</span>
        </Item>
      ))}
      <MetaRow>
        <MetaLabel>{t('performance.aiCenter.detail.sample')}</MetaLabel>
        <MetaValue>{insight.n}{insight.coverage != null ? ` · ${t('performance.aiCenter.detail.coverage')} %${Math.round(Number(insight.coverage) * 100)}` : ''}</MetaValue>
      </MetaRow>
      <MetaRow>
        <MetaLabel>{t('performance.aiCenter.detail.dataVersion')}</MetaLabel>
        <MetaValue>{t('performance.aiCenter.detail.dataVersionValue', { rule: insight.ruleVersion, config: insight.configVersion, date: formatDate(insight.dataAsOf, lang) })}</MetaValue>
      </MetaRow>
      {link ? (
        <MetaRow>
          <MetaLabel>{t('performance.aiCenter.detail.source')}</MetaLabel>
          <Button variant="ghost" data-action="open-source" onClick={() => onNavigate(link)}>{t('performance.aiCenter.detail.openSource')}</Button>
        </MetaRow>
      ) : null}
      <SectionTitle>{t('performance.aiCenter.detail.limitation')}</SectionTitle>
      <Hint>{t(`performance.aiCenter.explain.${rule}.limitation`, { defaultValue: t('performance.aiCenter.explain.default.limitation') })}</Hint>
      <SectionTitle>{t('performance.aiCenter.detail.possibleExplanation')}</SectionTitle>
      <Hint data-label="possible-explanation">{t(`performance.aiCenter.explain.${rule}.explanation`, { defaultValue: t('performance.aiCenter.explain.default.explanation') })}</Hint>
      {suggested ? (
        <MetaRow>
          <MetaLabel>{t('performance.aiCenter.detail.suggestedAction')}</MetaLabel>
          <MetaValue>{t(`performance.action.type.${suggested}`, { defaultValue: suggested })}</MetaValue>
        </MetaRow>
      ) : null}
    </>
  );
}

function EvidenceItems({ insight, onNavigate }) {
  const { t } = useTranslation();
  const items = insight?.evidence?.items || [];
  if (items.length === 0) return null;
  const names = insight.names || {};
  return (
    <>
      <SectionTitle>{t('performance.insight.items')}</SectionTitle>
      {items.map((it, i) => {
        if (it.stageKey) {
          return (
            <Item key={it.stageKey}>
              <span>{t(`performance.insight.stage.${it.stageKey}`, { defaultValue: it.stageKey })}</span>
              <span>{t('performance.insight.stageMedian', { recent: it.medianRecentHours, prior: it.medianPriorHours, nRecent: it.nRecent, nPrior: it.nPrior })}</span>
            </Item>
          );
        }
        const id = it.employeeId || it.departmentId || it.criticalPositionId || it.titleId;
        // P7a — kişi kalemi adı görünürse (yetki içi) E14 profiline gider; yöneticide kişi kümesi dışı kimlik sunucudan gelmez.
        const label = names[id] || '-';
        return (
          // eslint-disable-next-line react/no-array-index-key
          <Item key={id || i}>
            {it.employeeId && names[id] && onNavigate
              ? <Button variant="ghost" data-action="open-profile" onClick={() => onNavigate(profileLink(it.employeeId, insight.periodId))}>{label}</Button>
              : <span>{label}</span>}
            {it.count != null || it.delta != null
              ? <span>{[it.count != null ? it.count : null, it.delta != null ? `Δ ${it.delta}` : null].filter((x) => x != null).join(' · ')}</span>
              : null}
          </Item>
        );
      })}
    </>
  );
}

export default function InsightDetailDrawer({ insight, open, onClose, onChangeStatus, onOpenAction, onInvalidate, isHr = true }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const history = useHistory();
  const [actions, setActions] = useState([]);
  const navigate = (path) => { onClose?.(); history.push(path); };

  useEffect(() => {
    if (!open || !insight?.id) { setActions([]); return; }
    api.get('/v1/performance-actions', { params: { 'sourceInsightId[eq]': insight.id, pageSize: 50 } }).then((res) => {
      if (!res?.data?.status) return;
      const r = res.data.data;
      setActions(Array.isArray(r) ? r : (r?.data || []));
    });
  }, [open, insight?.id]);

  if (!insight) return null;
  const status = insight.status;
  const primary = status === 'new'
    ? { label: t('performance.insight.actions.review'), next: 'reviewing' }
    : status === 'actioned' ? { label: t('performance.insight.actions.close'), next: 'closed' } : null;
  const canAct = ['new', 'reviewing', 'actioned'].includes(status);
  const sev = SEVERITY_COLORS[insight.severity] || SEVERITY_COLORS.amber;
  const st = INSIGHT_STATUS_COLORS[status] || INSIGHT_STATUS_COLORS.new;
  const scopeName = insight.names?.[insight.scopeId];

  return (
    <GenericFormDrawer
      open={open}
      onClose={onClose}
      title={insightTitle(insight, t)}
      schema={[]}
      submitLabel={primary?.label || t('common.close', { defaultValue: 'Kapat' })}
      onSave={async () => (primary ? onChangeStatus(insight.id, primary.next) : true)}
    >
      {insight.evidenceChangedSinceReview ? <Notice>{t('performance.insight.staleNotice')}</Notice> : null}
      <Text>{insightBody(insight, t)}</Text>
      <MetaRow>
        <MetaLabel>{t('performance.insight.severity')}</MetaLabel>
        <Badge color={sev.color} bg={sev.bg}>{t(`performance.insight.severityValue.${insight.severity}`)}</Badge>
      </MetaRow>
      <MetaRow>
        <MetaLabel>{t('performance.insight.status')}</MetaLabel>
        <Badge color={st.color} bg={st.bg}>{t(`performance.insight.statusValue.${status}`)}</Badge>
      </MetaRow>
      <MetaRow>
        <MetaLabel>{t('performance.insight.scopeLabel')}</MetaLabel>
        <MetaValue>{t(`performance.insight.scope.${insight.scopeType}`)}{scopeName ? `: ${scopeName}` : ''}</MetaValue>
      </MetaRow>
      <MetaRow>
        <MetaLabel>{t('performance.insight.period')}</MetaLabel>
        <MetaValue>{insight.periodName || '-'}</MetaValue>
      </MetaRow>
      <MetaRow>
        <MetaLabel>{t('performance.insight.detectedAt')}</MetaLabel>
        <MetaValue>{formatDate(insight.detectedAt, lang)}</MetaValue>
      </MetaRow>
      <MetaRow>
        <MetaLabel>{t('performance.insight.dataAsOf')}</MetaLabel>
        <MetaValue>{formatDate(insight.dataAsOf, lang)}</MetaValue>
      </MetaRow>
      {status === 'invalid' && insight.invalidReason ? (
        <MetaRow>
          <MetaLabel>{t('performance.insight.invalidReason')}</MetaLabel>
          <MetaValue>{insight.invalidReason}</MetaValue>
        </MetaRow>
      ) : null}

      <EvidenceItems insight={insight} onNavigate={navigate} />
      <InsightExplain insight={insight} isHr={isHr} onNavigate={navigate} />

      <SectionTitle>{t('performance.insight.linkedActions')}</SectionTitle>
      {actions.length === 0 ? <MetaValue>{t('performance.insight.noActions')}</MetaValue> : actions.map((a) => (
        <Item key={a.id}>
          <span>{a.title}{a.ownerName ? ` · ${a.ownerName}` : ''}</span>
          <span>{t(`performance.action.statusValue.${a.status}`)}{a.outcomeNote ? ` · ${a.outcomeNote}` : ''}</span>
        </Item>
      ))}

      <Buttons>
        {canAct ? <Button onClick={() => onOpenAction(insight)}>{t('performance.insight.actions.openAction')}</Button> : null}
        {status !== 'invalid' ? <Button variant="outline" onClick={() => onInvalidate(insight)}>{t('performance.insight.actions.invalidate')}</Button> : null}
      </Buttons>
    </GenericFormDrawer>
  );
}
